import React, { useState, useEffect, useContext } from "react";
import { TextField, Autocomplete, Button, Snackbar, Alert } from "@mui/material";
import { FilterPanel } from "../../components/FilterPanel";
import { AppContext } from "../../layout/MainLayout";
import {
    getSlotList,
    addSlotConfiguration,
    removeSlotConfiguration,
} from "../../services/addConfiguration";

export default function AddConfiguration() {
    const { selectedLocation } = useContext(AppContext);

    const [filters, setFilters] = useState({});
    const [loading, setLoading] = useState(false);
    const [timeValue, setTimeValue] = useState(() => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    });

    const [slotOptions, setSlotOptions] = useState([]);
    const [startSlot, setStartSlot] = useState(null);
    const [endSlot, setEndSlot] = useState(null);
    const [count, setCount] = useState("");

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        const today = new Date();
        const timestamp = today.getTime();
        setFilters((prev) => ({
            ...prev,
            toTime: [timestamp],
        }));
    }, []);

    const handleTimeChange = (e) => {
        const value = e.target.value;
        setTimeValue(value);
        if (value) {
            const [year, month, day] = value.split("-").map(Number);
            const now = new Date();
            const timestamp = new Date(
                year,
                month - 1,
                day,
                now.getHours(),
                now.getMinutes(),
                now.getSeconds()
            ).getTime();
            setFilters((prev) => ({
                ...prev,
                toTime: [timestamp],
            }));
        } else {
            setFilters((prev) => ({
                ...prev,
                toTime: [],
            }));
        }
    };

    useEffect(() => {
        const fetchSlots = async () => {
            try {
                setLoading(true);
                const data = await getSlotList();

                const normalized = (data || []).map(slot => ({
                    id: slot,
                    label: slot,
                }));
                setSlotOptions(normalized);
            } catch (err) {
                console.error("Failed to fetch slot list:", err);
                setSlotOptions([]);
            } finally {
                setLoading(false);
            }
        };
        if (Object.keys(filters).length > 0) {
            fetchSlots();
        }
    }, [filters]);

    const handleClearAll = () => {
        setFilters({});
        setTimeValue("");
        setSlotOptions([]);
        setStartSlot(null);
        setEndSlot(null);
        setCount("");
    };

    const getTimestampWithCurrentTime = (date) => {
        const [year, month, day] = date.split("-").map(Number);
        const now = new Date();
        return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds()).getTime();
    };

    const handleAdd = async () => {
        if (!startSlot || !endSlot || !count) {
            setSnackbar({
                open: true,
                message: "Please select start slot, end slot and enter count",
                severity: "error",
            });
            return;
        }
        const timestamp = getTimestampWithCurrentTime(timeValue);
        const payload = {
            corporateId: Array.isArray(filters.corporate) ? filters.corporate[0] : filters.corporate || "",
            vehicleType: Array.isArray(filters.vehicleType) ? filters.vehicleType[0] : filters.vehicleType || "",
            regionId: filters.region?.[0]?.regionId || selectedLocation?.regionId || '',
            startSlotId: startSlot.id,
            endSlotId: endSlot.id,
            count: Number(count),
            timeSlot: timestamp,
        };
        try {
            await addSlotConfiguration(payload);
            setSnackbar({
                open: true,
                message: "Slot added successfully!",
                severity: "success",
            });
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to add slot configuration.",
                severity: "error",
            });
        }
    };

    const handleRemove = async () => {
        if (!startSlot || !endSlot || !count) {
            setSnackbar({
                open: true,
                message: "Please select start slot, end slot and enter count",
                severity: "error",
            });
            return;
        }
        const timestamp = getTimestampWithCurrentTime(timeValue);
        const payload = {
            corporateId: Array.isArray(filters.corporate) ? filters.corporate[0] : filters.corporate || "",
            vehicleType: Array.isArray(filters.vehicleType) ? filters.vehicleType[0] : filters.vehicleType || "",
            regionId: filters.region?.[0]?.regionId || selectedLocation?.regionId || '',
            startSlotId: startSlot.id,
            endSlotId: endSlot.id,
            count: Number(count),
            timeSlot: timestamp,
        };
        try {
            await removeSlotConfiguration(payload);
            setSnackbar({
                open: true,
                message: "Slot removed successfully!",
                severity: "success",
            });
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to remove slot configuration.",
                severity: "error",
            });
        }
    };

    const timeField = (
        <TextField
            label="Date"
            type="date"
            value={timeValue}
            onChange={handleTimeChange}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
        />
    );

    return (
        <div>
            <FilterPanel
                filters={filters}
                setFilters={setFilters}
                loading={loading}
                onApplyFilters={() => { }}
                onClearAll={handleClearAll}
                selectedLocation={selectedLocation}
                extraFields={timeField}
                excludeKeys={["dayOfWeek"]}
                showApplyButton={false}
            />

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "100px" }}>
                <div style={{ display: "flex", gap: "60px", alignItems: "center", justifyContent: "center" }}>
                    <Autocomplete
                        options={slotOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={startSlot}
                        onChange={(_, newValue) => setStartSlot(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Start Slot" size="small" />
                        )}
                        sx={{ width: 250 }}
                    />

                    <Autocomplete
                        options={slotOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={endSlot}
                        onChange={(_, newValue) => setEndSlot(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="End Slot" size="small" />
                        )}
                        sx={{ width: 250 }}
                    />

                    <TextField
                        label="Count"
                        type="number"
                        value={count}
                        onChange={(e) => setCount(e.target.value)}
                        size="small"
                        sx={{ width: 200 }}
                    />
                </div>

                <div style={{ display: "flex", gap: "80px", marginTop: "100px", justifyContent: "center" }}>
                    <Button
                        variant="contained"
                        sx={{ width: 160, backgroundColor: "#70B92F", "&:hover": { backgroundColor: "#5aa325" } }}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>

                    <Button
                        variant="contained"
                        sx={{ width: 160, backgroundColor: "#C23232", "&:hover": { backgroundColor: "#a82a2a" } }}
                        onClick={handleRemove}
                    >
                        Remove
                    </Button>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}